import {
  GraphCmsQuestionIdEnum,
  GraphCmsQuestionIdToAnswers,
  Page,
  ProgramNameEnum,
  ProgramStageEnum,
  ProgramSubStageEnum,
} from 'lib/constants';
import {
  AssessmentProgressStatus,
  ProgramData,
  QualificationQuestionsByYear,
  TaxCreditAssessmentGeneralHygraphId,
  TaxCreditAssessmentProgramsHygraphId,
  TaxCreditAssessments,
} from 'lib/interfaces';

export const DONE_ASSESSMENT_STATUSES: AssessmentProgressStatus[] = [
  AssessmentProgressStatus.READY_TO_SUBMIT,
  AssessmentProgressStatus.MS_REVIEW,
  AssessmentProgressStatus.CLIENT_REVIEW,
  AssessmentProgressStatus.FINISHED,
  AssessmentProgressStatus.OPTED_OUT,
];

/**
 * Single source of truth for whether an assessment section is done for
 * submission purposes. SKIPPED is deliberately NOT done: skip means defer,
 * so a skipped section keeps blocking submission until it is completed.
 * OPTED_OUT is done: opt-out is the submittable set-aside — the customer
 * declined the credit, so the section no longer blocks submission.
 */
export const isSectionDone = (
  assessment: Pick<TaxCreditAssessments, 'status'>,
): boolean =>
  !!assessment.status && DONE_ASSESSMENT_STATUSES.includes(assessment.status);

/**
 * Statuses that permanently take a section out of the filing: it will never
 * advance through MS_REVIEW → CLIENT_REVIEW → FINISHED. Post-submit dashboard
 * gates must filter these sections out before `every(...)`-ing over progress,
 * or a single such section blocks the post-submit states forever.
 *
 * NOTE: the pure-DQ predicates (`isAllAssessmentsDisqualified` /
 * `areAllAssessmentsDisqualified`) intentionally do NOT use this list — the
 * ineligible-messaging and KeyDates consumers need "all DQ" specifically.
 */
export const EXCLUDED_FROM_FILING_STATUSES: AssessmentProgressStatus[] = [
  AssessmentProgressStatus.DQ,
  AssessmentProgressStatus.OPTED_OUT,
];

export const isSectionExcludedFromFiling = (
  assessment: Pick<TaxCreditAssessments, 'status'>,
): boolean =>
  !!assessment.status &&
  EXCLUDED_FROM_FILING_STATUSES.includes(assessment.status);

/**
 * Program-level twin of isSectionDone, for call sites that only have the
 * Program entity (the share/invitee flow never loads assessment statuses):
 * a section is done when its survey is ready to submit, the customer opted
 * out of the credit (pre-submit; submission stamps it CANCELED like any
 * other cancel), or the program was disqualified (DQ nulls the substage).
 */
export const isProgramSectionDone = (
  program: Pick<ProgramData, 'stage' | 'subStage'>,
): boolean =>
  program.subStage ===
    ProgramSubStageEnum.EXPENSE_CLASSIFICATION_READY_TO_SUBMIT ||
  program.subStage ===
    ProgramSubStageEnum.EXPENSE_CLASSIFICATION_SURVEY_OPTED_OUT ||
  (program.subStage === null &&
    program.stage === ProgramStageEnum.DISQUALIFIED);

/**
 * "Viewed" is deliberately weaker than "done": a skipped section has been
 * seen and deliberately set aside, so next-step navigation may route past it
 * — but it still blocks submission (SKIPPED is not in isSectionDone).
 */
export const isProgramSectionViewed = (
  program: Pick<ProgramData, 'stage' | 'subStage'>,
): boolean =>
  isProgramSectionDone(program) ||
  program.subStage ===
    ProgramSubStageEnum.EXPENSE_CLASSIFICATION_SURVEY_SKIPPED;

/**
 * Maps an assessment (Hygraph id) to its survey page so incomplete/skipped
 * sections can link back to where the customer finishes them. R&D and any
 * unknown ids land on the first R&D survey page.
 */
export const getAssessmentSurveyUrl = (
  assessmentId: string,
  taxYear: number,
): string => {
  const assessmentSurveyRootPath = `/${Page.taxCredits}/${Page.assessment}/${taxYear}`;

  switch (assessmentId) {
    case TaxCreditAssessmentGeneralHygraphId.GENERAL_BUSINESS_DETAILS:
      return assessmentSurveyRootPath;
    case TaxCreditAssessmentProgramsHygraphId[
      ProgramNameEnum.FED_RETIREMENT_CREDIT
    ]:
      return `${assessmentSurveyRootPath}/${Page.retirementPlan}`;
    case TaxCreditAssessmentProgramsHygraphId[
      ProgramNameEnum.FED_EMPLOYER_HEALTHCARE
    ]:
      return `${assessmentSurveyRootPath}/${Page.healthcare}`;
    case TaxCreditAssessmentProgramsHygraphId[
      ProgramNameEnum.FED_DISABLED_ACCESS
    ]:
      return `${assessmentSurveyRootPath}/${Page.disabledAccess}`;
    default:
      return `${assessmentSurveyRootPath}/${Page.expenseClassificationCompanyDetails}`;
  }
};

export const completedCompanyDetailsSection = (
  qualificationQuestionsByYear: QualificationQuestionsByYear,
  taxYear: number,
) => {
  return (
    qualificationQuestionsByYear?.[taxYear][
      GraphCmsQuestionIdEnum.TIME_TRACKING
    ] !== undefined
  );
};

export const completedCloudSpendSection = (
  qualificationQuestions: QualificationQuestionsByYear,
  taxYear: number,
) => {
  const cloudSpendQuestion =
    qualificationQuestions[taxYear][
      GraphCmsQuestionIdEnum.SPEND_ANY_AMOUNT_CLOUD
    ];
  const cloudSpendYesAnswer =
    GraphCmsQuestionIdToAnswers[GraphCmsQuestionIdEnum.SPEND_ANY_AMOUNT_CLOUD]
      .YES;
  const cloudSpendRdPercentageQuestion =
    qualificationQuestions[taxYear][
      GraphCmsQuestionIdEnum.WHAT_PERCENT_CLOUD_RD
    ];

  // If the answer to the cloud spend question is yes, and the RD percentage question is answered, the section is complete
  return (
    cloudSpendQuestion !== undefined &&
    (cloudSpendQuestion !== cloudSpendYesAnswer ||
      (cloudSpendQuestion === cloudSpendYesAnswer &&
        cloudSpendRdPercentageQuestion !== undefined))
  );
};

export const completedSuppliesSpendSection = (
  qualificationQuestions: QualificationQuestionsByYear,
  taxYear: number,
) => {
  const suppliesSpendQuestion =
    qualificationQuestions[taxYear][
      GraphCmsQuestionIdEnum.SPEND_ANY_AMOUNT_SUPPLIES
    ];
  const suppliesSpendYesAnswer =
    GraphCmsQuestionIdToAnswers[
      GraphCmsQuestionIdEnum.SPEND_ANY_AMOUNT_SUPPLIES
    ].YES;
  const suppliesSpendRdPercentageQuestion =
    qualificationQuestions[taxYear][
      GraphCmsQuestionIdEnum.WHAT_PERCENT_SUPPLIES_RD
    ];

  // If the answer to the supplies spend question is yes, and the RD percentage question is answered, the section is complete
  return (
    suppliesSpendQuestion !== undefined &&
    (suppliesSpendQuestion !== suppliesSpendYesAnswer ||
      (suppliesSpendQuestion === suppliesSpendYesAnswer &&
        suppliesSpendRdPercentageQuestion !== undefined))
  );
};

export const completedSuppliesAndServicesSection = (
  qualificationQuestions: QualificationQuestionsByYear,
  taxYear: number,
) => {
  return (
    completedCloudSpendSection(qualificationQuestions, taxYear) &&
    completedSuppliesSpendSection(qualificationQuestions, taxYear)
  );
};
